import { useEffect, useRef, useState } from 'react'
import { Box, Card, Icon, RadioGroup, Typography } from '@mui/material'
import { ErrorMessage, Form, Formik } from 'formik'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import AdxButton from 'components/custom/AdxButton'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxRadioButton from 'components/custom/AdxRadioButton'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import { putLeaveAllocationData } from 'services/api/modules/staff-management/staffManagementDashboard'
import { AllocationComponent } from './AllocationComponent'

const MONTHLY_SCOPE_CURRENT = 'current_month'
const MONTHLY_SCOPE_REMAINING = 'remaining_months'

const monthlyScopeOptions = [
  {
    value: MONTHLY_SCOPE_CURRENT,
    label: 'Only this month',
    description: 'Update only the selected monthly allocation.',
  },
  {
    value: MONTHLY_SCOPE_REMAINING,
    label: 'This and future months',
    description: 'Future months start with the updated allocation as their full balance.',
  },
]

const isCarryForwardZero = (cf) => {
  const amount = Number(cf?.amount ?? 0)
  const balance = Number(cf?.balance ?? 0)
  return amount === 0 && balance === 0
}

const toAllocationRows = (rows = []) =>
  rows.map((item) => ({
    id: item?.id,
    amount: item?.entitlement?.amount ?? 0,
    balance: item?.entitlement?.balance ?? 0,
  }))

const numberSchema = Yup.number()
  .typeError('Please enter a valid number')
  .min(0, 'Value must be 0 or more')
  .required('Value is required')

const validationSchema = Yup.object().shape({
  yearly: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().required(),
      amount: numberSchema,
      balance: numberSchema,
    })
  ),
  monthly: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().required(),
      amount: numberSchema,
      balance: numberSchema,
    })
  ),
})

const AllocationHeader = ({ title }) => (
  <Box
    mb={2}
    sx={{
      display: 'flex',
      width: '100%',
      alignItems: 'center',
    }}
  >
    <Box sx={{ width: 2 / 4 }}>
      <Typography variant="body1" color="light-gray-text.main">
        {title}
      </Typography>
    </Box>
    <Box sx={{ width: 1 / 4 }}>
      <Typography variant="body1" color="light-gray-text.main">
        Balance (Days)
      </Typography>
    </Box>
    <Box sx={{ width: 1 / 4 }}>
      <Typography variant="body1" color="light-gray-text.main">
        Allocation (Days)
      </Typography>
    </Box>
  </Box>
)

const LeaveAllocation = ({ data, staffId, onAllocationSaved }) => {
  const formikRef = useRef()
  const [disable, setDisable] = useState(true)
  const [scopeModalOpen, setScopeModalOpen] = useState(false)
  const [selectedMonthlyScope, setSelectedMonthlyScope] = useState(MONTHLY_SCOPE_CURRENT)
  const [pendingSave, setPendingSave] = useState(null)
  const [initialValues, setInitialValues] = useState({
    yearly: [],
    monthly: [],
  })

  const yearly = data?.leave?.yearly ?? []
  const monthly = data?.leave?.monthly ?? []
  const hasLeaveAllocation = yearly.length > 0 || monthly.length > 0

  const mutation = useMutation((values) => putLeaveAllocationData(staffId, values), {
    onSuccess: (response) => {
      onAllocationSaved(response?.data)
      setDisable(true)
      setScopeModalOpen(false)
      setPendingSave(null)
      setSelectedMonthlyScope(MONTHLY_SCOPE_CURRENT)
    },
    onError: (error) => {
      const message = error?.data?.message || 'Unable to update leave allocation. Please try again.'

      if (error?.status === 422 && error?.data?.errors && formikRef.current) {
        Object.keys(error.data.errors).forEach((key) => {
          formikRef.current.setFieldError(key, error.data.errors[key][0])
        })
      }

      toast.error(message)
    },
  })

  useEffect(() => {
    setInitialValues({
      yearly: toAllocationRows(data?.leave?.yearly ?? []),
      monthly: toAllocationRows(data?.leave?.monthly ?? []),
    })
  }, [data])

  const handleCancel = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  const getChangedMonthlyIds = (values) => {
    const initialById = new Map((initialValues.monthly ?? []).map((row) => [row.id, row]))

    return (values.monthly ?? [])
      .filter((row) => {
        const initialRow = initialById.get(row.id)
        if (!initialRow) return true

        return (
          Number(row.amount) !== Number(initialRow.amount) ||
          Number(row.balance) !== Number(initialRow.balance)
        )
      })
      .map((row) => row.id)
  }

  const buildScopedPayload = (values, changedMonthlyIds, scope) => ({
    ...values,
    monthly: (values.monthly ?? []).map((row) =>
      changedMonthlyIds.includes(row.id) ? { ...row, scope } : row
    ),
  })

  const handleSave = (values) => {
    const changedMonthlyIds = getChangedMonthlyIds(values)

    if (changedMonthlyIds.length === 0) {
      mutation.mutate(values)
      return
    }

    setPendingSave({
      values,
      changedMonthlyIds,
    })
    setSelectedMonthlyScope(MONTHLY_SCOPE_CURRENT)
    setScopeModalOpen(true)
  }

  const handleScopeModalClose = () => {
    if (mutation.isLoading) return
    setScopeModalOpen(false)
    setPendingSave(null)
    setSelectedMonthlyScope(MONTHLY_SCOPE_CURRENT)
  }

  const handleConfirmMonthlyScope = () => {
    if (!pendingSave) return

    mutation.mutate(
      buildScopedPayload(
        pendingSave.values,
        pendingSave.changedMonthlyIds,
        selectedMonthlyScope
      )
    )
  }

  const renderRows = ({ rows, values, touched, errors, handleChange, handleBlur, group }) => {
    if (rows.length === 0) {
      return (
        <Typography variant="body2" color="light-gray-text.main">
          No {group} leave allocation.
        </Typography>
      )
    }

    return rows.map((item, index) => {
      const cf = item?.carry_forward
      const showCF = cf && !isCarryForwardZero(cf)

      return (
        <Box key={item?.id} sx={{ display: 'flex', flexDirection: 'column' }}>
          <AllocationComponent
            disabled={disable}
            name={item?.leave_policy ?? 'Untitled'}
            balanceName={`${group}.${index}.balance`}
            amountName={`${group}.${index}.amount`}
            onChange={handleChange}
            onBlur={handleBlur}
            item={{
              balance: values[group]?.[index]?.balance ?? 0,
              amount: values[group]?.[index]?.amount ?? 0,
            }}
            balanceErrorMessage={
              <ErrorMessage name={`${group}.${index}.balance`} component={AdxErrorMessage} />
            }
            amountErrorMessage={
              <ErrorMessage name={`${group}.${index}.amount`} component={AdxErrorMessage} />
            }
            isBalanceErrorShown={touched[group]?.[index]?.balance && errors[group]?.[index]?.balance}
            isAmountErrorShown={touched[group]?.[index]?.amount && errors[group]?.[index]?.amount}
          />

          {showCF && (
            <AllocationComponent
              isSubRow
              disabled
              name={`Carry Forward${cf?.from_year ? ` (${cf.from_year})` : ''}`}
              subLabel={cf?.expiry_date ? `Expiry: ${cf.expiry_date}` : null}
              item={{
                balance: cf?.balance ?? 0,
                amount: cf?.amount ?? 0,
              }}
            />
          )}
        </Box>
      )
    })
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      innerRef={formikRef}
      onSubmit={handleSave}
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit, resetForm }) => (
        <Form>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              alignItems: 'center',
              borderColor: 'gray-line.main',
              p: 2,
            }}
          >
            <Typography color="light-gray-text.main" fontWeight={500}>
              Leave Allocation
            </Typography>
          </Box>

          <Box
            sx={{
              rowGap: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              borderBottom: 1,
              borderColor: 'gray-line.main',
            }}
          >
            <Card sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} variant="none">
              <AllocationHeader title="Yearly Allocation" />
              {renderRows({
                rows: yearly,
                values,
                touched,
                errors,
                handleChange,
                handleBlur,
                group: 'yearly',
              })}
            </Card>

            <Card sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} variant="none">
              <AllocationHeader title="Monthly Allocation" />
              {renderRows({
                rows: monthly,
                values,
                touched,
                errors,
                handleChange,
                handleBlur,
                group: 'monthly',
              })}
            </Card>

            {disable && (
              <Box>
                <Typography>
                  You can manage leave allocation by going to: Company Management &gt; Leave tab
                  &gt; Import External Data
                  <br />
                  <br />
                  Download the CSV file, fill in the details, and upload it to update employee
                  leave allocation.
                </Typography>
              </Box>
            )}

            {!disable && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <AdxButton
                  type="button"
                  variant={'outlined'}
                  disabled={mutation.isLoading}
                  onClick={() => handleCancel(resetForm)}
                  sx={{ width: 0.2 }}
                >
                  Cancel
                </AdxButton>
                <AdxButton
                  type="button"
                  variant={'contained'}
                  disabled={mutation.isLoading}
                  isLoading={mutation.isLoading}
                  onClick={handleSubmit}
                  sx={{ width: 0.2 }}
                >
                  Save
                </AdxButton>
              </Box>
            )}
          </Box>

          {disable && hasLeaveAllocation && (
            <Box sx={{ p: 0.5 }}>
              <AdxButton
                type="button"
                variant={'ghost'}
                sx={{
                  pl: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
                onClick={() => setDisable(false)}
              >
                <Icon sx={{ fontSize: '15px', color: 'alternate.main' }}>edit</Icon>
                <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                  Edit leave allocation
                </Typography>
              </AdxButton>
            </Box>
          )}

          <AdxFormModal
            open={scopeModalOpen}
            onClose={handleScopeModalClose}
            handleClose={handleScopeModalClose}
            title="Apply monthly changes"
            subtitle="Choose how this monthly allocation update should be applied."
            icon={<Icon>calendar_month</Icon>}
            sxModal={{ width: { xs: 'calc(100% - 24px)', md: 560 } }}
            enableFooter
            cancelText="Cancel"
            actionText="Apply changes"
            handleAction={handleConfirmMonthlyScope}
            isLoading={mutation.isLoading}
            actionButtonDisabled={mutation.isLoading}
            responsiveScroll
          >
            <Box sx={{ width: 1 }}>
              <RadioGroup
                value={selectedMonthlyScope}
                onChange={(event) => setSelectedMonthlyScope(event.target.value)}
                sx={{ gap: 1 }}
              >
                {monthlyScopeOptions.map((option) => (
                  <AdxFormControlLabel
                    key={option.value}
                    type="outlined"
                    control={<AdxRadioButton type="no-radio" />}
                    label={option.label}
                    description={option.description}
                    value={option.value}
                    selected={selectedMonthlyScope}
                    disabled={mutation.isLoading}
                    sx={{
                      borderRadius: '8px',
                      alignItems: 'flex-start',
                      minHeight: '72px',
                      px: 1,
                      '& .MuiFormControlLabel-label': {
                        width: 1,
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Box>
          </AdxFormModal>
        </Form>
      )}
    </Formik>
  )
}

export default LeaveAllocation
